import React, { useImperativeHandle, useRef } from "react";
import { Handler, useDrag, useGesture } from "@use-gesture/react";
import { ActiveBox } from "../../../active-box";
import { BottomMenuPanel } from "../../../bottom-menu-panel";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../../../bottom-menu/buttons";
import { PlaceholderImage } from "../../../placeholder";
import { useIsMobileDevice } from "../../../../hooks/useIsMobileDevice";

import "./symbols-list-panel.css";

export interface ISymbolsListPanelRef {
	scrollIntoView(symbolCode: string, scrollAnimated?: boolean): void;
	getSymbolSelectionPanelEl(): HTMLElement;
}

export interface IPanelSymbol {
	url: string;
	code: string;
	isWhite: boolean;
}

interface ISymbolsListPanelProps {
	symbols: readonly IPanelSymbol[];
	selectedSymbolCode?: string;
	onClick(code: string): void;
	onDrag?: Handler<"drag">;
}

interface ISymbolsThumbProps {
	url: string;
	isActive?: boolean;
	onClick?(): void;
}

function Thumb({
	url,
	isActive,
	isWhite,
	onClick,
	...gestureHandlers // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
}: ISymbolsThumbProps & ReturnType<ReturnType<typeof useDrag>>) {
	return (
		<ActiveBox
			className={`symbols-list-panel__img-container ${
				isWhite ? "symbols-list-panel__img-container-gray" : ""
			}`}
			isActive={isActive}
		>
			<PlaceholderImage
				onClick={onClick}
				className="symbols-list-panel__img"
				src={url}
				loading="lazy"
				{...gestureHandlers}
			/>
		</ActiveBox>
	);
}

export const SymbolsListPanel = ({
	selectedSymbolCode,
	symbols,
	onClick,
	onDrag,
	ref,
}: ISymbolsListPanelProps & { ref?: React.Ref<ISymbolsListPanelRef> }) => {
	const isMobileDevice = useIsMobileDevice();

	const gestures = useGesture(
		{
			onDrag,
		},
		{
			drag: {
				pointer: { touch: true },
				preventScrollAxis: "x",
				axis: isMobileDevice ? "y" : undefined,
				filterTaps: isMobileDevice ? false : true,
			},
		},
	);

	const scrollableRef = useRef<IBottomMenuButtonsRef>(null);
	const symbolSelectionPanelRef = useRef<HTMLDivElement>(null);

	useImperativeHandle(
		ref,
		() => ({
			scrollIntoView: (code, animate = true) => {
				if (!scrollableRef.current) return;

				const index = symbols.findIndex((symbol) => symbol.code === code);

				scrollableRef.current.scrollIntoView(index, {
					animate,
				});
			},
			getSymbolSelectionPanelEl: () => {
				return symbolSelectionPanelRef.current as HTMLElement;
			},
		}),
		[symbols],
	);

	return (
		<BottomMenuPanel ref={symbolSelectionPanelRef} className="symbols-list-panel">
			<BottomMenuButtons ref={scrollableRef}>
				{symbols.map((symbol) => (
					<Thumb
						key={symbol.code}
						url={symbol.url}
						isActive={selectedSymbolCode === symbol.code}
						isWhite={symbol.isWhite}
						{...gestures(symbol)}
						onClick={() => onClick(symbol.code)}
					/>
				))}
			</BottomMenuButtons>
		</BottomMenuPanel>
	);
};
SymbolsListPanel.displayName = "SymbolsListPanel";
